/**
 * TanStack Query hooks for server state. Auth stays in Redux only.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type { Grievance, GrievanceUpdate, Image } from "@/data/grievances";
import {
  getCases,
  getMyCases,
  getCaseById,
  getCaseUpdates,
  createCase,
  addCaseImage,
  getCaseImageById,
  getOperators,
  type AddCaseImageParams,
} from "./apiClient";

export const queryKeys = {
  cases: ["cases"] as const,
  myCases: (deviceId: string) => ["myCases", deviceId] as const,
  case: (id: string) => ["case", id] as const,
  caseUpdates: (caseId: string) => ["caseUpdates", caseId] as const,
  operators: ["operators"] as const,
  caseImages: (ids: { id: string }[]) => ["caseImages", ids.map((i) => i.id).sort().join(",")] as const,
};

export function useCases(enabled = true) {
  return useQuery({
    queryKey: queryKeys.cases,
    queryFn: getCases,
    enabled,
  });
}

export function useMyCases(deviceId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.myCases(deviceId ?? ""),
    queryFn: () => getMyCases(deviceId!),
    enabled: Boolean(deviceId) && enabled,
  });
}

export function useCase(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.case(id ?? ""),
    queryFn: () => getCaseById(id!),
    enabled: Boolean(id) && enabled,
  });
}

export function useCaseUpdates(caseId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.caseUpdates(caseId ?? ""),
    queryFn: () => getCaseUpdates(caseId!),
    enabled: Boolean(caseId) && enabled,
  });
}

export function useOperators(enabled = true) {
  return useQuery({
    queryKey: queryKeys.operators,
    queryFn: getOperators,
    enabled,
  });
}

export function useCaseImages(ids: { id: string }[], enabled = true) {
  return useQuery({
    queryKey: queryKeys.caseImages(ids),
    queryFn: () => getCaseImageById(ids),
    enabled: ids.length > 0 && enabled,
  });
}

export function useCreateCase(
  options?: UseMutationOptions<unknown, Error, unknown>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCase,
    onSuccess: () => {
      // Invalidate both "all cases" and "my cases" queries
      queryClient.invalidateQueries({ queryKey: queryKeys.cases });
      queryClient.invalidateQueries({ queryKey: ["myCases"] });
    },
    ...options,
  });
}

export function useAddCaseImage(
  options?: UseMutationOptions<unknown, Error, AddCaseImageParams>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addCaseImage,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases });
      queryClient.invalidateQueries({ queryKey: queryKeys.case(variables.entityId) });
    },
    ...options,
  });
}
